/*
 * Copyright (c) 2025, Gather
 * https://gather.life
 */

'use client';

import { Grid2 as Grid } from '@mui/material';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { isResponseError } from '@marketplace/gather-api';
import { type components } from '@marketplace/gather-api/types/marketplace-vehicle-catalog-mgmt-public';
import { captureException } from '@marketplace/gather-platform/observability';
import {
  PageContentsSectionMainPanel,
  PageContentsSectionSidePanel,
} from '@marketplace/gather-ui/layouts';

import { StopPrefetch } from '@/components/navigation/stop-prefetch.component';
import { useUserId } from '@/rest/hooks/use-user-id';
import useVehicleListingSearch from '@/rest/hooks/use-vehicle-listing-search';

import { VehicleGrid } from '../components';
import { useCatalogSearchStore } from '../components/catalog-search-store.provider';
import { SearchView } from '../components/search/search.view';
import { useWaitForAuth } from '../hooks/use-wait-for-auth';

import CatalogVehiclesErrorView from './catalog-vehicles-error.view';
import CatalogVehiclesLoadingView from './catalog-vehicles-loading.view';
import FiltersContainer from './filters-container.view';

type CatalogRequestBody = components['schemas']['CatalogRequestBody'];

const paddingLeft = { md: 4 };
const spacing = { base: 3, sm: 6 };

/**
 * Isolates the search response and state from the rest of the page so that
 * it doesn't cause the filters to render when they don't need to.
 *
 * WARN: This component needs to be used within a error boundary
 *
 * @throws Error Will throw an error if the search query has an error
 */
function SearchResponseContainer(): ReactNode {
  const { query, updatePage } = useCatalogSearchStore((state) => ({
    query: state.query,
    updatePage: state.updatePage,
  }));

  // TODO(randy): The cast is required here since the Open API type does not match the state type.
  // It *should* be safe but the API types need to be fixed ASAP
  const searchState = useVehicleListingSearch(query as CatalogRequestBody);

  const [placeholderLoading, setPlaceholderLoading] = useState<'pagination' | 'results-loading'>(
    'pagination'
  );

  const isInitialLoading = searchState.status === 'pending';

  const onPageChange = useCallback(
    (page: number) => {
      setPlaceholderLoading('pagination');

      // Convert 1-indexed UI selection back to 0-indexed for backend
      updatePage(page - 1);
    },
    [updatePage, setPlaceholderLoading]
  );

  const handleSubmit = useCallback(() => {
    setPlaceholderLoading('results-loading');
  }, []);

  // Convert 0-indexed backend page to 1-indexed for UI display
  const currentPage = query.page + 1;

  if (searchState.isPlaceholderData) {
    return (
      <>
        <SearchView disabled loading onSubmit={handleSubmit} />
        <VehicleGrid
          currentPage={currentPage}
          items={searchState.data.items}
          loadingPattern={placeholderLoading}
          searchHistoryId={null}
          totalItems={searchState.data.totalItems}
          totalPages={searchState.data.totalPages}
          onPageChange={onPageChange}
        />
      </>
    );
  }

  if (searchState.status === 'pending' || searchState.isFetching) {
    return (
      <>
        <SearchView loading disabled={false} onSubmit={handleSubmit} />
        <VehicleGrid
          currentPage={currentPage}
          items={null}
          loadingPattern='initial'
          searchHistoryId={null}
          totalItems={null}
          totalPages={null}
          onPageChange={onPageChange}
        />
      </>
    );
  }

  // WARN: This component needs to be used with an error boundary
  if (searchState.status === 'error') {
    const { error }: { error: unknown } = searchState;

    captureException(error instanceof Error ? error : new Error(String(error)));

    if (error instanceof Error || isResponseError(error)) {
      throw searchState.error;
    }

    throw new Error('failed to fetch search results');
  }

  return (
    <>
      <SearchView disabled={isInitialLoading} loading={false} onSubmit={handleSubmit} />
      <VehicleGrid
        currentPage={currentPage}
        items={searchState.data.items}
        loadingPattern='none'
        searchHistoryId={searchState.data?.searchHistoryId ?? ''}
        totalItems={searchState.data.totalItems}
        totalPages={searchState.data.totalPages}
        onPageChange={onPageChange}
      />
    </>
  );
}

export default function CatalogVehiclesView({
  hydratedWithUserId,
}: {
  hydratedWithUserId: null | string;
}): ReactNode {
  const { searchHistoryId } = useCatalogSearchStore((state) => ({
    searchHistoryId: state.meta.searchHistoryId,
  }));

  // Just need to increment the value, doesn't matter if it rolls over
  // or not. Just needs to be different between each render.
  const [resetKey, setResetKey] = useState(1);
  const resetKeys = useMemo(() => [resetKey], [resetKey]);

  // NOTE: On the error page, if the user enters a new query/search the error
  // boundary needs to reset.
  const handleSubmit = useCallback(() => {
    setResetKey((key) => key + 1);
  }, []);

  const fallback = useMemo(
    () => (
      <CatalogVehiclesErrorView
        SearchComponent={<SearchView disabled={false} loading={false} onSubmit={handleSubmit} />}
      />
    ),
    [handleSubmit]
  );

  const userId = useUserId();

  // NOTE: If the server hydrated the store using a user id, then we want to wait
  // until the client has had the time to initialize the auth provider. Otherwise,
  // the search and everything will initialize without an user then when the auth
  // provider loads, it will use the cache. This causes a lot of useless rendering
  // so it's better to avoid it altogether.
  const waitForAuth = useWaitForAuth(hydratedWithUserId);

  const onError = useCallback(
    (error: unknown) => {
      captureException(error instanceof Error ? error : new Error(String(error)), {
        hydratedWithUserId,
        searchHistoryId,
        userId,
        waitForAuth,
      });
    },
    [hydratedWithUserId, searchHistoryId, userId, waitForAuth]
  );

  if (waitForAuth) {
    return <CatalogVehiclesLoadingView />;
  }

  return (
    <ErrorBoundary fallback={fallback} resetKeys={resetKeys} onError={onError}>
      <StopPrefetch />
      <PageContentsSectionSidePanel minWidth={290} size={3}>
        <FiltersContainer searchHistoryId={searchHistoryId} />
      </PageContentsSectionSidePanel>
      <PageContentsSectionMainPanel>
        <Grid container direction='column' paddingLeft={paddingLeft} spacing={spacing}>
          <SearchResponseContainer />
        </Grid>
      </PageContentsSectionMainPanel>
    </ErrorBoundary>
  );
}
